import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Save, X, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminEmployees() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/employees"); setRows(data || []); }
    catch { toast.error("Could not load employees"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function remove(emp) {
    if (!window.confirm(`Delete ${emp.name}?`)) return;
    try { await api.delete(`/employees/${emp.id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  }

  return (
    <div data-testid="admin-employees-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Staff</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Employees</h1>
          <p className="text-white/50 mt-2 text-sm">{rows.length} employee{rows.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => setEditing({ mode: "create" })} data-testid="emp-add-btn" className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]">
          <Plus size={14} /> Add Employee
        </button>
      </div>

      {loading ? <div className="text-white/40 text-sm">Loading…</div> : (
        rows.length === 0 ? (
          <div className="bg-[#0e0e0e] border border-white/10 p-8 text-center text-white/40 text-sm">No employees yet.</div>
        ) : (
          <div className="bg-[#0e0e0e] border border-white/10 divide-y divide-white/5">
            {rows.map((e) => (
              <div key={e.id} className="px-4 py-3 flex items-center gap-3" data-testid={`emp-row-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium">{e.name}{!e.active && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-300">Inactive</span>}</div>
                  {e.note && <div className="text-[11px] text-white/50">{e.note}</div>}
                </div>
                <button onClick={() => setEditing({ mode: "edit", employee: e })} data-testid={`emp-edit-${e.id}`} className="p-1.5 text-white/60 hover:text-[#C9963F]" title="Edit"><Pencil size={14} /></button>
                <button onClick={() => remove(e)} data-testid={`emp-delete-${e.id}`} className="p-1.5 text-white/60 hover:text-red-400" title="Delete"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )
      )}

      {editing && <EmployeeDialog initial={editing.employee} mode={editing.mode} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EmployeeDialog({ initial, mode, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    note: initial?.note || "",
    active: initial?.active ?? true,
    pin: "",
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.name.trim()) return toast.error("Name required");
    if (mode === "create" && (!form.pin || form.pin.length < 4)) return toast.error("PIN min 4 chars");
    setSaving(true);
    try {
      if (mode === "edit" && initial) {
        const payload = { name: form.name.trim(), note: form.note, active: form.active };
        if (form.pin && form.pin.length >= 4) payload.pin = form.pin;
        await api.patch(`/employees/${initial.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/employees", { name: form.name.trim(), pin: form.pin, note: form.note, active: form.active });
        toast.success("Created");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  const inputCls = "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-[#0e0e0e] border border-[#C9963F]/40 max-w-md w-full p-6 sm:p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="emp-dialog">
        <button onClick={onCancel} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={18} /></button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">{mode === "edit" ? "Edit" : "New"} Employee</div>
        <h3 className="font-serif-brand text-2xl mb-6">{form.name || "—"}</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Name *</label>
            <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="emp-name-input" autoFocus />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{mode === "edit" ? "Reset PIN (leave blank to keep)" : "PIN * (min 4)"}</label>
            <input type="text" inputMode="numeric" className={inputCls} value={form.pin} onChange={(e) => set("pin", e.target.value)} data-testid="emp-pin-input" placeholder={mode === "edit" ? "Leave blank to keep current PIN" : "e.g. 1234"} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Note</label>
            <input className={inputCls} value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Role / phone / etc." />
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="emp-active-toggle" />
            <span className="text-sm text-white/70">Active (can log in)</span>
          </label>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onCancel} className="text-white/60 hover:text-white px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="emp-save-btn" className="inline-flex items-center gap-2 bg-[#C9963F] text-black font-medium px-5 py-2 hover:bg-[#A67A2D] text-sm disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
